
import { supabase } from '@/integrations/supabase/client';
import { Subscription } from '@/types/subscription';
import { ClientStatus, MembershipType } from '@/types/types';
import { apiBaseUrl, appBaseUrl } from '@/utils/config';
import { mapDbSubscriptionsToDomain } from '@/utils/subscriptionMapper';
import { getUserProfile as getUserProfileWrapper, updateUserProfile as updateUserProfileWrapper } from '@/services/edgeFunctionWrappers/userManagementWrapper';
import { getUserSubscriptions, getUserSubscriptionsBulk } from '@/services/edgeFunctionWrappers/subscriptionsManagementWrapper';
import { getUserGymId } from '@/utils/gymAuth';

export interface ProfileAuth {
  id: string;
  email: string;
  lastVisit:Date;
}

const apiUrl = apiBaseUrl;

/**
 * Get user profile data from the database
 */
export const getUserProfile = async (userId: string): Promise<any> => {
  try {
    // For backward compatibility, get gymId from context if not provided
    const { getUserGymId } = await import('@/utils/gymAuth');
    const gymId = getUserGymId();
    const profile = await getUserProfileWrapper(userId, gymId || '');
    return profile;
  } catch (error) {
    console.error('Error in getUserProfile:', error);
    return null;
  }
};

export async function changeAuthEmail(userId: string, newEmail: string) {
  const sessionResponse = await supabase.auth.getSession();
  const accessToken = sessionResponse.data.session?.access_token;
  const BASE_URL = appBaseUrl;

  const emailResponse = await fetch(`${apiUrl}/functions/v1/quick-processor`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      user_id: userId,
      new_email: newEmail,
    }),
  });

  let result: any = {};
  try {
    result = await emailResponse.json();

    await supabase.auth.signInWithOtp({
      email: newEmail,
      options: {
        emailRedirectTo: `${BASE_URL}/set-password`
      }
    });
  } catch (e) {
    // Ignore JSON parse error — happens if the body is empty
  }

  if (!emailResponse.ok) {
    throw new Error(result?.error || `Erreur HTTP ${emailResponse.status}`);
  }

  return result;
}


/**
 * Update user profile data
 */
export const updateUserProfile = async (userId: string, data: any): Promise<boolean> => {
  try {
    // For backward compatibility, get gymId from context if not provided
    const { getUserGymId } = await import('@/utils/gymAuth');
    const gymId = getUserGymId();
    // Le wrapper renvoie null (sans lever) quand la session a expiré :
    // renvoyer `true` à l'aveugle faisait afficher « Profil mis à jour »
    // alors que rien n'avait été écrit.
    const profile = await updateUserProfileWrapper(userId, gymId || '', data);
    return profile != null;
  } catch (error) {
    console.error('Error in updateUserProfile:', error);
    return false;
  }
};

/**
 * Upload a profile photo and return the URL
 */
export const uploadProfilePhoto = async (userId: string, file: File): Promise<string | null> => {
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;
    
    // Upload to storage
    const { error: uploadError } = await supabase
      .storage
      .from('profile_photos')
      .upload(filePath, file);
    
    if (uploadError) {
      console.error('Error uploading profile photo:', uploadError);
      return null;
    }
    
    // Get the public URL
    const { data } = supabase
      .storage
      .from('profile_photos')
      .getPublicUrl(filePath);
    
    return data.publicUrl;
  } catch (error) {
    console.error('Error in uploadProfilePhoto:', error);
    return null;
  }
};

/**
 * Get the email address of a user from auth.users table
 * (since email is not stored in user_profiles)
 */
export const getUserEmail = async (userId: string): Promise<ProfileAuth | null> => {
  try {
    const sessionResponse = await supabase.auth.getSession();
    const accessToken = sessionResponse.data.session?.access_token;
    
    // Fetch email using the edge function
    const emailResponse = await fetch(`${apiUrl}/functions/v1/get-user-details`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ userId: userId }),
    });

    const emailData = await emailResponse.json();
    console.log("emailData:",emailData)
    return emailData?.users[0] || null;
  } catch (error) {
    console.error('Error in getUserEmail:', error);
    return null;
  }
};

export const getUsersSubscriptions = async (userIds: string[]): Promise<Subscription[]> => {
  try {
    const gymId = getUserGymId();
    
    if (!gymId) {
      console.error("No gym_id available for getUsersSubscriptions");
      return [];
    }

    // ✅ UN SEUL appel au lieu de N appels
    const allSubscriptions = await getUserSubscriptionsBulk(userIds, gymId);
    
    return mapDbSubscriptionsToDomain(allSubscriptions);
  } catch (error) {
    console.error("Error in getUsersSubscriptions:", error);
    return [];
  }
};

export async function getUsersEmailByUserIds(userIds: string[]): Promise<Record<string, string>> {
  if (!Array.isArray(userIds) || userIds.length === 0) return {};
  try {
    const { data, error } = await supabase.functions.invoke('get-user-details', {
      body: { "userIds": userIds },
    });
    
    if (error) throw error;

    console.log("📧 Response from get-user-details:", data);

    // If map exists (new format), use it directly
    if (data?.map) {
      console.log("📧 Using map from response:", data.map);
      return data.map as Record<string, string>;
    }

    // Fallback: construct map from users array (backward compatibility)
    const map: Record<string, string> = {};
    if (data?.users && Array.isArray(data.users)) {
      data.users.forEach((user: any) => {
        if (user?.id && user?.email) {
          map[user.id] = user.email;
        }
      });
      console.log("📧 Constructed map from users:", map);
    }
    
    return map;
  } catch (e) {
    console.error("❌ getUsersEmailByUserIds failed", e);
    return {};
  }
}

/**
 * Check if a user has already booked a class for a specific date and time
 * This function accounts for cancelled bookings and allows rebooking
 */
export const hasActiveBooking = async (userId: string, classId: string, classDate: string): Promise<boolean> => {
  try {
    // Look for any 'upcoming' bookings for this class and date
    const { data, error } = await supabase
      .from('class_bookings')
      .select('id, status')
      .eq('user_id', userId)
      .eq('class_id', classId)
      .eq('class_date', classDate)
      .eq('status', 'upcoming')
      .maybeSingle();
    
    if (error) {
      console.error('Error checking booking status:', error);
      throw error;
    }
    
    // User has an active booking if there's a record with 'upcoming' status
    return !!data;
  } catch (error) {
    console.error('Error in hasActiveBooking:', error);
    return false;
  }
};

/**
 * Get all bookings for a user, class and date regardless of status
 */
export const getBookingsForClassAndUser = async (userId: string, classId: string, classDate: string): Promise<any[]> => {
  try {
    const { data, error } = await supabase
      .from('class_bookings')
      .select('*')
      .eq('user_id', userId)
      .eq('class_id', classId)
      .eq('class_date', classDate);
    
    if (error) {
      console.error('Error fetching bookings:', error);
      return [];
    }
    
    return data || [];
  } catch (error) {
    console.error('Error in getBookingsForClassAndUser:', error);
    return [];
  }
};

export interface DeleteUserResult {
  success: boolean;
  /** `complete` = compte auth supprimé ; `partial` = retiré de cette salle seulement. */
  deletionType?: 'complete' | 'partial';
  remainingGyms?: number;
  /** Le serveur a-t-il réellement supprimé le compte auth ? */
  authDeleted?: boolean;
  /**
   * Vrai quand le nombre de salles de l'adhérent n'a pas pu être déterminé :
   * on est resté en suppression ciblée par prudence, donc l'adresse e-mail
   * reste prise. À signaler à l'utilisateur, pas à masquer.
   */
  scopeUnknown?: boolean;
}

/**
 * Nombre de salles auxquelles l'adhérent est rattaché.
 * Renvoie `null` si l'information n'a pas pu être obtenue — auquel cas on ne
 * doit surtout pas supposer « une seule salle » et déclencher une suppression
 * globale.
 *
 * ⚠️ Ce compte ne peut PAS être lu depuis le navigateur.
 * `user-management` (comme l'appel direct de repli) utilise un client porteur
 * du JWT de l'appelant : les politiques RLS de `user_gym_assignments`
 * (`20251018100041_*.sql:24-36`) ne laissent voir à un administrateur que les
 * lignes `gym_id` où il a le rôle `admin`. Un adhérent inscrit dans les salles
 * A et B est donc vu « mono-salle » par l'administrateur de A, et l'omission
 * de `gymId` bascule le serveur en `DELETE_PLAN_GLOBAL` +
 * `auth.admin.deleteUser` : le compte est détruit, y compris pour la salle B.
 *
 * On demande donc le compte au serveur, qui le calcule en service-role.
 * `delete-user-everywhere` en `dryRun` n'écrit rien (toutes les branches
 * testent `dryRun` avant d'écrire) et renvoie, quand `gymId` est fourni,
 * `remainingGyms` = nombre TOTAL de lignes `user_gym_assignments` de
 * l'adhérent, sans filtre de salle (`index.ts:359-366`). En dryRun rien n'a
 * encore été supprimé : c'est donc bien le nombre de salles de l'adhérent.
 */
async function countUserGyms(
  userId: string,
  gymId: string,
  accessToken: string
): Promise<number | null> {
  try {
    const { data, error } = await supabase.functions.invoke('delete-user-everywhere', {
      headers: { Authorization: `Bearer ${accessToken}` },
      body: { userId, gymId, dryRun: true }
    });

    if (error) {
      console.error('[deleteUser] Sonde de portée (dryRun) en échec :', error);
      return null;
    }
    if (!data?.success) {
      console.error('[deleteUser] Sonde de portée refusée :', data?.error);
      return null;
    }

    const remaining = data.remainingGyms;
    if (typeof remaining !== 'number' || !Number.isFinite(remaining) || remaining <= 0) {
      // 0 ou valeur absente : on ne sait pas conclure (adhérent déjà détaché ?),
      // on refuse de basculer en suppression globale.
      console.warn('[deleteUser] Portée indéterminable, remainingGyms =', remaining);
      return null;
    }

    // `remainingGyms` compte TOUTES les affectations de l'adhérent, sans dire
    // laquelle. `UNIQUE(user_id, gym_id)` garantit 1 ligne = 1 salle, donc
    // `remaining === 1` veut dire « une seule salle » — mais pas forcément
    // CELLE-CI. Le plan ciblé du dryRun compte, lui, les affectations filtrées
    // sur `gymId` (`index.ts:274-290`, `filterByGym`). Si l'adhérent n'a aucune
    // affectation dans la salle de l'appelant, la salle unique est une AUTRE
    // salle : une suppression globale détruirait le compte d'un adhérent qu'on
    // n'administre pas. On refuse de conclure.
    const assignmentsInThisGym = data.summary?.deletedByTable?.user_gym_assignments;
    if (typeof assignmentsInThisGym === 'number' && assignmentsInThisGym < 1) {
      console.warn(
        '[deleteUser] Aucune affectation dans la salle courante : portée non concluante.'
      );
      return null;
    }

    return remaining;
  } catch (error) {
    console.error('[deleteUser] Impossible de compter les salles de l\'adhérent:', error);
    return null;
  }
}

/**
 * Supprime un adhérent via `delete-user-everywhere`.
 *
 * L'appel passait **toujours** `gymId`, donc le serveur restait en plan
 * « ciblé » : le compte auth n'était jamais supprimé, `user_profiles` n'était
 * pas nettoyé et la réponse annonçait « partial » même pour un adhérent
 * mono-salle. L'adresse e-mail restait prise et la recréation (ou le ré-import
 * CSV) échouait.
 *
 * On choisit donc la portée d'après le nombre réel de salles de l'adhérent,
 * obtenu du serveur en service-role (cf. `countUserGyms`) et jamais d'une
 * lecture navigateur, qui est tronquée par RLS :
 * - une seule salle  -> appel **sans** `gymId` = suppression complète ;
 * - plusieurs salles -> appel avec `gymId` = retrait de cette salle seulement ;
 * - portée indéterminable -> repli prudent sur le ciblé, signalé à l'appelant.
 *
 * Le repli en cas de doute est toujours le ciblé : il est réversible (l'e-mail
 * reste pris, l'administrateur en est averti), alors qu'une suppression
 * globale à tort est définitive.
 */
export const deleteUser = async (userId: string, gymId?: string): Promise<DeleteUserResult> => {
  try {
    // Get gymId from context if not provided
    const effectiveGymId = gymId || getUserGymId();

    if (!effectiveGymId) {
      console.error('Error in deleteUser: No gym_id available');
      return { success: false };
    }

    // Get session for auth header
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error('Error in deleteUser: No session available');
      return { success: false };
    }

    const gymCount = await countUserGyms(userId, effectiveGymId, session.access_token);
    const scopeUnknown = gymCount === null;
    // Mono-salle => suppression globale (le serveur ne supprime le compte auth
    // que lorsque `gymId` est absent du corps de la requête).
    const isSingleGym = gymCount !== null && gymCount <= 1;

    console.log(
      `[deleteUser] user ${userId} — salles: ${gymCount ?? 'inconnu'} — portée: ${isSingleGym ? 'globale' : 'ciblée'}`
    );

    const { data, error } = await supabase.functions.invoke('delete-user-everywhere', {
      headers: { Authorization: `Bearer ${session.access_token}` },
      body: {
        userId,
        // Champ omis en suppression globale : c'est sa seule présence qui fait
        // basculer le serveur en plan ciblé.
        ...(isSingleGym ? {} : { gymId: effectiveGymId }),
        dryRun: false
      }
    });

    if (error) {
      console.error('Error deleting user:', error);
      return { success: false };
    }

    if (!data?.success) {
      console.error('Delete user failed:', data?.error);
      return { success: false };
    }

    console.log(`[deleteUser] User ${userId} deleted successfully (type: ${data.deletionType})`);

    return {
      success: true,
      // On rapporte ce que le serveur a fait, pas ce qu'on a demandé.
      deletionType: data.deletionType,
      remainingGyms: data.remainingGyms,
      authDeleted: data.authDeleted === true,
      scopeUnknown,
    };
  } catch (error) {
    console.error('Error in deleteUser:', error);
    return { success: false };
  }
};
